import { useEffect, useState } from 'react';
import {
  DAY, MONTHS, addDays, alternatives, at, calc, competing, dateOf, dayInfo, dayInfoText, dayOfWeek, fDT, fDate, fShort, fTime, inr,
  monthRange, rangeVerdict, span, startOfDay, toInstant,
} from '@nandanam/domain';
import { useStore, type AppStore } from '../app/store';
import { Icon } from '../ui/icons';
import { PageHead, StatusPill } from '../ui/components';
import { BAR, calItems, layoutBars, pkgLabel, type BarKind } from '../app/selectors';
import { recordActions } from './Record';
import { conflictText } from '../overlays/kit';

type Sel = { kind: 'days'; from: string; to: string } | { kind: 'item'; id: string; rec: boolean } | null;
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function Calendar() {
  const S = useStore(), today = dateOf(S.now), p = S.route.params;
  const focus = p.focus && /^\d{4}-\d\d-\d\d$/.test(p.focus) ? p.focus : null;
  const ym = p.m && /^\d{4}-\d\d$/.test(p.m) ? p.m : (focus ?? today).slice(0, 7);
  const year = Number(ym.slice(0, 4)), month0 = Number(ym.slice(5, 7)) - 1;
  const [history, setHistory] = useState(false);
  const [sel, setSel] = useState<Sel>(focus ? { kind: 'days', from: focus, to: focus } : null);
  const [drag, setDrag] = useState<string | null>(null);
  useEffect(() => { if (focus) setSel({ kind: 'days', from: focus, to: focus }); }, [focus]);
  // On phones the panel sits under the month, so bring it into view once a selection is made.
  useEffect(() => { if (sel && !drag && innerWidth <= 640) document.querySelector('.cal-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [JSON.stringify(sel), drag]);
  useEffect(() => { const up = () => setDrag(null); addEventListener('mouseup', up); return () => removeEventListener('mouseup', up); }, []);

  const { start: mStart, end: mEnd } = monthRange(year, month0);
  const gridStart = addDays(mStart, -((dayOfWeek(mStart) + 6) % 7));
  const weeks: string[][] = [];
  for (let d = gridStart; d < mEnd; ) { const w: string[] = []; for (let i = 0; i < 7; i++, d = addDays(d, 1)) w.push(d); weeks.push(w); }
  const shift = (n: number) => { const m = (month0 + n + 12) % 12, y = year + Math.floor((month0 + n) / 12); S.nav('calendar', { m: `${y}-${String(m + 1).padStart(2, '0')}` }); setSel(null); };
  // Swipe left or right across the month to change month (phones).
  const [touch, setTouch] = useState<{ x: number; y: number } | null>(null);
  const swipeStart = (e: React.TouchEvent) => { const t = e.touches[0]; setTouch(t && e.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null); };
  const swipeEnd = (e: React.TouchEvent) => {
    const t = e.changedTouches[0]; if (!touch || !t) return;
    const dx = t.clientX - touch.x, dy = t.clientY - touch.y; setTouch(null);
    if (Math.abs(dx) > 60 && Math.abs(dx) > 2 * Math.abs(dy)) shift(dx < 0 ? 1 : -1);
  };
  const opts = { now: S.now, bufferHours: S.state.settings.bufferHours };

  /* summary */
  const monthDays: string[] = []; for (let d = mStart; d < mEnd; d = addDays(d, 1)) monthDays.push(d);
  const infos = new Map(monthDays.map(d => [d, dayInfo(S.state, d, opts)]));
  const booked = monthDays.filter(d => infos.get(d)!.state !== 'free').length;
  const freeLeft = monthDays.filter(d => d >= today && infos.get(d)!.state === 'free').length;
  const holds = S.state.records.filter(r => S.phase(r) === 'hold').length;
  const revenue = S.state.records.filter(r => ['confirmed', 'completed'].includes(r.status)).filter(r => { const d = dateOf(span(r).start); return d >= mStart && d < mEnd; }).reduce((t, r) => t + calc(r).total, 0);
  const openEnq = S.state.records.filter(r => S.phase(r) === 'enquiry' && (() => { const d = dateOf(span(r).start); return d >= mStart && d < mEnd; })());

  const inSel = (d: string) => sel?.kind === 'days' && d >= sel.from && d <= sel.to;
  const down = (d: string) => { setDrag(d); setSel({ kind: 'days', from: d, to: d }); };
  const enter = (d: string) => { if (drag) setSel({ kind: 'days', from: d < drag ? d : drag, to: d < drag ? drag : d }); };

  return (
    <div className="page wide">
      <PageHead icon="calendar" title={
        <span className="cal-nav">
          <button className="icon-btn" aria-label="Previous month" onClick={() => shift(-1)}><Icon name="left" size={16} /></button>
          <button className="cal-month" title="Back to this month" onClick={() => { S.nav('calendar', { focus: today }); }}>{MONTHS[month0]} {year}</button>
          <button className="icon-btn" aria-label="Next month" onClick={() => shift(1)}><span style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}><Icon name="left" size={16} /></span></button>
        </span>} sub="Availability · tap a day, or drag across days to check · swipe the calendar to change month">
        <label className="toggle"><input type="checkbox" checked={history} onChange={e => setHistory(e.target.checked)} />Show expired and cancelled</label>
        <button className="btn" disabled={!S.canEdit} onClick={() => S.open('block', { date: sel?.kind === 'days' ? sel.from : undefined })}><Icon name="lock" size={15} />Block dates</button>
        <button className="btn primary" onClick={() => S.open('new', sel?.kind === 'days' ? { date: sel.from, endDate: sel.to } : {})}><Icon name="plus" size={15} />New enquiry</button>
      </PageHead>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {[['Days in use', `${booked}`, `of ${monthDays.length} in ${MONTHS[month0]} · booked, held or blocked`], ['Free days remaining', `${freeLeft}`, 'whole days with nothing booked'], ['Active holds', `${holds}`, 'block dates until they expire', () => S.nav('enquiries', { filter: 'hold' })], ['Booked revenue', inr(revenue), 'bookings starting this month', () => S.nav('bookings', { filter: 'current' })]].map(([k, v, n, go]) => {
          const inner = <><div className="muted" style={{ fontSize: 12.5 }}>{k as string}</div><b className="num" style={{ fontSize: 24, fontFamily: 'var(--font-heading)' }}>{v as string}</b><div className="note">{n as string}</div></>;
          return go ? <button key={k as string} type="button" className="kpi cal-kpi" onClick={go as () => void}>{inner}</button> : <div key={k as string} className="kpi cal-kpi">{inner}</div>;
        })}
      </div>

      <div className="legend-row">
        {(['event', 'stay', 'hold', 'completed', 'block', 'maint', 'expired'] as BarKind[]).map(k => (
          <span key={k} className="legend"><span style={{ background: BAR[k].bg, border: `1px ${BAR[k].bs} ${BAR[k].bc}` }} />{k === 'block' ? 'Personal / private' : k === 'expired' ? 'Expired / cancelled' : BAR[k].word}</span>
        ))}
        <span className="legend"><span style={{ background: '#fff', border: '1px dotted #8a9696' }} />Open enquiries (don't block){openEnq.length ? ` · ${openEnq.length}` : ''}</span>
      </div>

      <div className="cal-layout">
        <section className="card cal-card" onMouseLeave={() => setDrag(null)} onTouchStart={swipeStart} onTouchEnd={swipeEnd}>
          <div className="cal-head">{WD.map(w => <div key={w}>{w}</div>)}</div>
          {weeks.map(w => {
            const from = startOfDay(w[0]!), items = calItems(S, from, from + 7 * DAY, history), bars = layoutBars(items, from, 7);
            const lanes = bars.reduce((m, b) => Math.max(m, b.lane + 1), 0);
            return (
              <div key={w[0]} className="cal-week">
                <div className="cal-days">
                  {w.map(d => {
                    const out = d < mStart || d >= mEnd, info = out ? null : infos.get(d)!, isToday = d === today, wk = [0, 6].includes(dayOfWeek(d));
                    const tag = !info || d < today ? '' : info.state === 'free' ? 'Free' : info.state === 'partial' ? dayInfoText(info, fTime).replace('Free ', '') : '';
                    return (
                      <button key={d} className={`cal-day ${out ? 'out' : ''} ${isToday ? 'today' : ''} ${wk ? 'wk' : ''} ${inSel(d) ? 'sel' : ''} ${d < today ? 'past' : ''}`}
                        onMouseDown={() => down(d)} onMouseEnter={() => enter(d)} aria-label={fDate(d, { year: true })}>
                        <span className="dn num">{Number(d.slice(8))}</span>
                        {tag && <span className={`tag ${info!.state}`}>{tag}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="cal-bars" style={{ height: lanes * 28 }}>
                  {bars.map(b => {
                    const st = BAR[b.k];
                    return (
                      <button key={b.id} className={`bar ${sel?.kind === 'item' && sel.id === b.id ? 'on' : ''}`} title={`${b.title} — ${b.sub}`} onClick={() => setSel({ kind: 'item', id: b.id, rec: b.rec })}
                        style={{ left: `calc(${b.left}% + 3px)`, width: `calc(${b.width}% - 6px)`, top: b.lane * 28, height: 24, background: st.bg, border: `1px ${st.bs} ${st.bc}`, color: st.fg, borderRadius: 8 }}>
                        <Icon name={st.icon} size={12} /><span className="ellipsis" style={{ textDecoration: b.k === 'expired' || b.k === 'cancelled' ? 'line-through' : 'none' }}>{b.cl ? '… ' : ''}{b.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
        <aside className="card cal-panel">
          {!sel && <div className="muted" style={{ fontSize: 14 }}><h3 className="card-sub" style={{ marginBottom: 8 }}>Check dates</h3>Click a day, or drag across several days, to see whether they are free. Click a bar to see that booking or block.</div>}
          {sel?.kind === 'days' && <DaysPanel S={S} from={sel.from} to={sel.to} />}
          {sel?.kind === 'item' && (sel.rec ? <RecordPanel S={S} id={sel.id} /> : <BlockPanel S={S} id={sel.id} onGone={() => setSel(null)} />)}
        </aside>
      </div>
    </div>
  );
}

function DaysPanel({ S, from, to }: { S: AppStore; from: string; to: string }) {
  const s = S.state.settings, opts = { now: S.now, bufferHours: s.bufferHours };
  const v = rangeVerdict(S.state, from, to, opts);
  const start = at(from, s.eventStart), end = at(to, s.eventEnd);
  const alts = v.state === 'available' || v.state === 'past' ? [] : alternatives(S.state, start, end, { ...opts, n: 3 });
  const comp = competing(S.state, `${from}T00:00`, `${addDays(to, 1)}T00:00`);
  const verdict = { available: ['Available', 'good-box'], partly: ['Partly available — check times', 'warn-box'], unavailable: ['Not available', 'error-box'], past: ['Includes past dates', 'info-box'] }[v.state];
  const full = v.state === 'unavailable' || v.state === 'past';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="kicker-sm">Selected dates</div>
      <h3 style={{ fontSize: 22 }}>{from === to ? fDate(from, { year: true }) : `${fDate(from)} – ${fDate(to)}`}</h3>
      <div className={verdict[1]}><b>{verdict[0]}</b></div>
      <div className="kv">{v.days.map(d => <div key={d.date}><span>{fDate(d.date)}</span><span style={{ fontWeight: 600 }}>{dayInfoText(d.info, fTime)}</span></div>)}</div>
      {v.conflicts.length > 0 && <div><div className="kicker-sm" style={{ marginBottom: 6 }}>What is already there</div>{v.conflicts.map(c => <button key={c.id} className="chip-btn" style={{ margin: '0 6px 6px 0' }} onClick={() => (c.kind === 'record' ? S.nav('booking', { id: c.id }) : undefined)}>{conflictText(S, c)}</button>)}</div>}
      {alts.length > 0 && <div><div className="kicker-sm" style={{ marginBottom: 6 }}>Nearest free dates</div><div className="chips">{alts.map(a => <button key={a.start} className="chip" onClick={() => S.nav('calendar', { focus: dateOf(toInstant(a.start)) })}>{fShort(a.start, a.end)}</button>)}</div></div>}
      {comp.length > 0 && <div className="info-box">{comp.length} open enquir{comp.length > 1 ? 'ies' : 'y'} for these dates ({comp.map(r => S.cust(r.cust).name).join(', ')}). Enquiries don't block — first to hold wins.</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button className="btn primary" onClick={() => S.open('new', { date: from, endDate: to })}>New enquiry</button>
        <button className="btn" disabled={!S.canEdit || v.state === 'past'} onClick={() => S.open('block', { date: from })}>Block dates</button>
        <button className="btn" disabled={full || !S.canEdit} onClick={() => S.open('new', { date: from, endDate: to, status: 'hold' })}>Place hold</button>
        <button className="btn" disabled={full || !S.canEdit} onClick={() => S.open('new', { date: from, endDate: to, status: 'confirmed' })}>New booking</button>
      </div>
    </div>
  );
}

function RecordPanel({ S, id }: { S: AppStore; id: string }) {
  const r = S.rec(id);
  if (!r) return null;
  const k = calc(r), sp = span(r), acts = recordActions(S, r), c = S.cust(r.cust);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}><StatusPill phase={S.phase(r)} /><span className="muted num" style={{ fontSize: 12 }}>{S.num(r)}</span></div>
      <h3 style={{ fontSize: 22 }}>{c.name}</h3>
      <div className="kv">
        <div><span>When</span><span>{fShort(sp.start, sp.end)}</span></div>
        <div><span>From</span><span>{fDT(sp.start)}</span></div><div><span>Until</span><span>{fDT(sp.end)}</span></div>
        <div><span>Package</span><span>{pkgLabel(r)}</span></div>
        <div><span>Guests</span><span>{r.guests}</span></div>
        <div><span>Total</span><span>{k.pending ? 'To be agreed' : inr(k.total)}</span></div>
        {k.balance > 0 && <div><span>Balance</span><span style={{ color: 'var(--bad-text)' }}>{inr(k.balance)}</span></div>}
        {r.hold && S.phase(r) === 'hold' && <div><span>Hold expires</span><span>{fDT(r.hold.expires)}</span></div>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button className="btn primary" onClick={() => S.nav('booking', { id })}>Open full record</button>
        {[acts.primary, ...acts.secondary].filter(Boolean).map(a => <button key={a!.label} className="btn" disabled={!S.canEdit && !/WhatsApp|Print|message|quote|instructions|thank/i.test(a!.label)} onClick={a!.go}>{a!.label}</button>)}
      </div>
    </div>
  );
}

function BlockPanel({ S, id, onGone }: { S: AppStore; id: string; onGone: () => void }) {
  const b = S.state.blocks.find(x => x.id === id);
  const [busy, setBusy] = useState(false);
  if (!b) return null;
  const remove = async () => {
    if (!confirm(`Remove the ${b.type} block for ${fShort(b.start, b.end)}?`)) return;
    setBusy(true);
    const x = await S.act('block.remove', `block:${id}`, { id }, 'Block removed — dates are free');
    setBusy(false);
    if (x.ok) onGone(); else S.toast(x.error!);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="kicker-sm">Blocked</div>
      <h3 style={{ fontSize: 22 }}>{b.type}</h3>
      <div className="kv"><div><span>From</span><span>{fDT(b.start)}</span></div><div><span>Until</span><span>{fDT(b.end)}</span></div>{b.note && <div><span>Note</span><span>{b.note}</span></div>}</div>
      <button className="btn danger" style={{ alignSelf: 'flex-start' }} disabled={busy || !S.canEdit} onClick={remove}><Icon name="trash" size={14} />Remove block</button>
    </div>
  );
}
